/* eslint-disable react/prop-types */
import { Route, Routes } from "react-router-dom";
import { RequireAuth } from "./auth/RequireAuth";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/Login";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { AppLayout } from "./layouts/AppLayout";
import { CadastrosHome } from "./pages/CadastrosHome";
import { EntitiesPage } from "./pages/cadastros/EntitiesPage";
import { SuppliersPage } from "./pages/cadastros/SuppliersPage";
import { ClientsPage } from "./pages/cadastros/ClientsPage";
import { BanksPage } from "./pages/cadastros/BanksPage";
import { BankAccountsPage } from "./pages/cadastros/BankAccountsPage";
import { CostCentersPage } from "./pages/cadastros/CostCentersPage";
import { AccountsPage } from "./pages/cadastros/AccountsPage";
import { CategoriesPage } from "./pages/cadastros/CategoriesPage";
import { OpexCategoriesPage } from "./pages/cadastros/OpexCategoriesPage";
import { CardBrandsPage } from "./pages/cadastros/CardBrandsPage";
import { FinanceiroHome } from "./pages/FinanceiroHome";
import { PayablesPage } from "./pages/financeiro/PayablesPage";
import { ReceivablesPage } from "./pages/financeiro/ReceivablesPage";
import { MovementsPage } from "./pages/financeiro/MovementsPage";
import { CashFlowPage } from "./pages/financeiro/CashFlowPage";
import { BudgetsPage } from "./pages/financeiro/BudgetsPage";
import { TransfersPage } from "./pages/financeiro/TransfersPage";
import { BoletoImportPage } from "./pages/financeiro/BoletoImportPage";
import { BoletoImportListPage } from "./pages/financeiro/BoletoImportListPage";
import { BoletoImportDetailPage } from "./pages/financeiro/BoletoImportDetailPage";
import { ExecDashboardPage } from "./pages/dashboards/ExecDashboardPage";
import { OpsDashboardPage } from "./pages/dashboards/OpsDashboardPage";
import { GovDashboardPage } from "./pages/dashboards/GovDashboardPage";
import { IndicesDashboardPage } from "./pages/dashboards/IndicesDashboardPage";
import { BudgetDashboardPage } from "./pages/dashboards/BudgetDashboardPage";
import { PaymentsDashboardPage } from "./pages/dashboards/PaymentsDashboardPage";
import { AuditLogsPage } from "./pages/audit/AuditLogsPage";
import { AuditSummaryPage } from "./pages/audit/AuditSummaryPage";
import { OpenFinanceConnectionsPage } from "./pages/financeiro/OpenFinanceConnectionsPage";
import { OpenFinanceReconciliationPage } from "./pages/financeiro/OpenFinanceReconciliationPage";
import { OpenFinanceSyncRunsPage } from "./pages/financeiro/OpenFinanceSyncRunsPage";
import { CardsPage } from "./pages/financeiro/CardsPage";
import { InvoicesPage } from "./pages/financeiro/InvoicesPage";
import { InvoiceDetailPage } from "./pages/financeiro/InvoiceDetailPage";
import { CreditCardRecurringPage } from "./pages/financeiro/CreditCardRecurringPage";
import { CreditCardReconcilePage } from "./pages/financeiro/CreditCardReconcilePage";
import { PartnersPage } from "./pages/pessoas/PartnersPage";
import { PartnerTransactionsPage } from "./pages/pessoas/PartnerTransactionsPage";
import { EmployeesPage } from "./pages/pessoas/EmployeesPage";
import { FuncoesPage } from "./pages/pessoas/FuncoesPage";
import { ContractsPage } from "./pages/financeiro/ContractsPage";
import { ContractRenewalsPage } from "./pages/financeiro/ContractRenewalsPage";
import { IndicesPage } from "./pages/indices/IndicesPage";
import { TasksPage } from "./pages/governanca/TasksPage";
import { TasksDashboardPage } from "./pages/governanca/TasksDashboardPage";
import { TasksConfigPage } from "./pages/governanca/TasksConfigPage";
import { NotificationsPage } from "./pages/governanca/NotificationsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { AccessDeniedPage } from "./pages/AccessDeniedPage";
import { BudgetVsActualPage } from "./pages/reports/BudgetVsActualPage";
import { DREPage } from "./pages/reports/DREPage";
import { BalancoPage } from "./pages/reports/BalancoPage";
import { DFCPage } from "./pages/reports/DFCPage";
import { ReportsCashNeed } from "./pages/reports/ReportsCashNeed";
import { CalendarPage } from "./pages/governanca/CalendarPage";
import { TaskRecurringPage } from "./pages/governanca/TaskRecurringPage";
import { MetaAdsImportPage } from "./pages/integracoes/MetaAdsImportPage";
import { OFXImportPage } from "./pages/financeiro/OFXImportPage";
import { FinanceIntegrityValidationPage } from "./pages/admin/FinanceIntegrityValidationPage";
import { UsersPage } from "./pages/configuracoes/UsersPage";
import { PaymentMethodsPage } from "./pages/configuracoes/PaymentMethodsPage";
import { AccessProfilesPage } from "./pages/configuracoes/AccessProfilesPage";
import { useAvailableDashboards } from "./hooks/useAvailableDashboards";
import { RequirePermission } from "./auth/RequirePermission";
import { useAnyPermission } from "./auth/usePermission";

// eslint-disable-next-line react/prop-types
const RequireDashboard: React.FC<{
  permission: string;
  children: React.ReactElement;
}> = ({ permission, children }) => {
  const { data, loading } = useAvailableDashboards();
  if (loading) {
    return (
      <div className="container py-5">
        <div className="alert alert-light border">Carregando...</div>
      </div>
    );
  }
  if (!data.includes(permission)) {
    return <AccessDeniedPage />;
  }
  return children;
};

// eslint-disable-next-line react/prop-types
const RequireAnyPermission: React.FC<{
  permissions: string[];
  children: React.ReactElement;
}> = ({ permissions, children }) => {
  const allowed = useAnyPermission(permissions);
  if (!allowed) {
    return <AccessDeniedPage />;
  }
  return children;
};

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="cadastros" element={<CadastrosHome />} />
        <Route path="financeiro" element={<FinanceiroHome />} />
        <Route path="cadastros/entidades" element={<EntitiesPage />} />
        <Route path="cadastros/fornecedores" element={<SuppliersPage />} />
        <Route path="cadastros/clientes" element={<ClientsPage />} />
        <Route path="cadastros/bancos" element={<BanksPage />} />
        <Route path="cadastros/contas-bancarias" element={<BankAccountsPage />} />
        <Route path="cadastros/centros-custo" element={<CostCentersPage />} />
        <Route path="cadastros/plano-contas" element={<AccountsPage />} />
        <Route path="cadastros/categorias" element={<CategoriesPage />} />
        <Route path="cadastros/categorias-opex" element={<OpexCategoriesPage />} />
        <Route path="cadastros/bandeiras-cartao" element={<CardBrandsPage />} />
        <Route path="financeiro/contas-pagar" element={<PayablesPage />} />
        <Route path="financeiro/contas-receber" element={<ReceivablesPage />} />
        <Route path="financeiro/movimentacoes" element={<MovementsPage />} />
        <Route path="financeiro/transferencias" element={<TransfersPage />} />
        <Route path="financeiro/fluxo-caixa" element={<CashFlowPage />} />
        <Route path="financeiro/orcamentos" element={<BudgetsPage />} />
        <Route path="financeiro/boletos/importar" element={<BoletoImportPage />} />
        <Route
          path="financeiro/boletos/importar/:id"
          element={<BoletoImportDetailPage />}
        />
        <Route path="financeiro/boletos" element={<BoletoImportListPage />} />
        <Route path="financeiro/boletos/:id" element={<BoletoImportDetailPage />} />
        <Route path="financeiro/cartoes" element={<CardsPage />} />
        <Route
          path="financeiro/cartoes/recorrentes"
          element={
            <RequirePermission permission="FIN_CREDITCARD_RECURRING_VIEW">
              <CreditCardRecurringPage />
            </RequirePermission>
          }
        />
        <Route
          path="financeiro/cartoes/reconciliacao"
          element={
            <RequirePermission permission="FIN_CC_RECONCILE_VIEW">
              <CreditCardReconcilePage />
            </RequirePermission>
          }
        />
        <Route path="financeiro/faturas" element={<InvoicesPage />} />
        <Route path="financeiro/faturas/:id" element={<InvoiceDetailPage />} />
        <Route path="financeiro/contratos" element={<ContractsPage />} />
        <Route path="financeiro/renovacoes" element={<ContractRenewalsPage />} />
        <Route
          path="financeiro/openfinance-conexoes"
          element={<OpenFinanceConnectionsPage />}
        />
        <Route
          path="financeiro/openfinance-reconciliacao"
          element={<OpenFinanceReconciliationPage />}
        />
        <Route
          path="financeiro/openfinance-sync"
          element={<OpenFinanceSyncRunsPage />}
        />
        <Route path="pessoas/socios" element={<PartnersPage />} />
        <Route path="pessoas/socios/:id" element={<PartnerTransactionsPage />} />
        <Route path="pessoas/funcionarios" element={<EmployeesPage />} />
        <Route path="pessoas/funcoes" element={<FuncoesPage />} />
        <Route
          path="dash/exec"
          element={
            <RequireDashboard permission="DASHBOARD_FINANCEIRO_GERAL">
              <ExecDashboardPage />
            </RequireDashboard>
          }
        />
        <Route
          path="dash/ops"
          element={
            <RequireDashboard permission="DASHBOARD_CAIXA">
              <OpsDashboardPage />
            </RequireDashboard>
          }
        />
        <Route
          path="dash/gov"
          element={
            <RequireDashboard permission="DASHBOARD_FINANCEIRO_GERAL">
              <GovDashboardPage />
            </RequireDashboard>
          }
        />
        <Route
          path="dash/indices"
          element={
            <RequireDashboard permission="DASHBOARD_FINANCEIRO_GERAL">
              <IndicesDashboardPage />
            </RequireDashboard>
          }
        />
        <Route
          path="dash/orcamento"
          element={
            <RequireDashboard permission="DASHBOARD_ORCAMENTO">
              <BudgetDashboardPage />
            </RequireDashboard>
          }
        />
        <Route
          path="dash/pagamentos"
          element={
            <RequireDashboard permission="DASHBOARD_CARTOES">
              <PaymentsDashboardPage />
            </RequireDashboard>
          }
        />
        <Route path="auditoria/logs" element={<AuditLogsPage />} />
        <Route path="auditoria/resumo" element={<AuditSummaryPage />} />
        <Route path="governanca/tarefas" element={<TasksPage />} />
        <Route
          path="governanca/tarefas/dashboard"
          element={
            <RequirePermission permission="TASK_DASHBOARD_VIEW">
              <TasksDashboardPage />
            </RequirePermission>
          }
        />
        <Route
          path="governanca/tarefas/configuracoes"
          element={
            <RequirePermission permission="TASK_CONFIG_MANAGE">
              <TasksConfigPage />
            </RequirePermission>
          }
        />
        <Route path="governanca/recorrencias" element={<TaskRecurringPage />} />
        <Route path="governanca/notificacoes" element={<NotificationsPage />} />
        <Route path="governanca/calendario" element={<CalendarPage />} />
        <Route path="financeiro/importar-ofx" element={<OFXImportPage />} />
        <Route
          path="financeiro/importar-meta"
          element={
            <RequireAnyPermission
              permissions={["FIN_META_IMPORT_VIEW", "FIN_META_IMPORT_CREATE"]}
            >
              <MetaAdsImportPage />
            </RequireAnyPermission>
          }
        />
        <Route
          path="admin/validacoes-financeiras"
          element={
            <RequirePermission permission="AUDIT_READ">
              <FinanceIntegrityValidationPage />
            </RequirePermission>
          }
        />
        <Route path="perfil" element={<ProfilePage />} />
        <Route path="perfil/senha" element={<ChangePasswordPage />} />
        <Route path="indices" element={<IndicesPage />} />
        <Route path="relatorios/orcado-realizado" element={<BudgetVsActualPage />} />
        <Route path="relatorios/orcamento" element={<BudgetDashboardPage />} />
        <Route path="relatorios/dre" element={<DREPage />} />
        <Route path="relatorios/balanco" element={<BalancoPage />} />
        <Route path="relatorios/dfc" element={<DFCPage />} />
        <Route
          path="relatorios/necessidade-caixa"
          element={<ReportsCashNeed />}
        />
        <Route path="configuracoes/usuarios" element={<UsersPage />} />
        <Route path="configuracoes/perfis-acesso" element={<AccessProfilesPage />} />
        <Route path="configuracoes/formas-pagamento" element={<PaymentMethodsPage />} />
        <Route path="payment-methods" element={<PaymentMethodsPage />} />
        {/* Catch-all route for 404 */}
        <Route
          path="*"
          element={
            <div className="container py-5">
              <div className="alert alert-warning">
                <h4>Página não encontrada</h4>
                <p>A página que você está procurando não existe.</p>
                <a href="/" className="btn btn-primary">
                  Voltar para o início
                </a>
              </div>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
